import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye, Pencil, FileText, Paperclip, ChevronDown, Trash2, Mail } from 'lucide-react';
import { StatusPill } from './ui/StatusPill';
import { STATUS_LIST } from '../lib/constants';

const MENU_WIDTH = 220;
const ESTIMATED_MENU_HEIGHT = 440; // ~13 itens * 32 + paddings/dividers/header
const VIEWPORT_MARGIN = 12;

export function RowActions({
  onView, onEdit, onPdf, onAttach, onDelete, onStatusChange, onSendNF,
  hasAnexoNota,
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Calcula posicao do menu (renderizado em portal -> body, position fixed)
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Estimativa primeira; depois corrigimos se necessario com a altura real
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - VIEWPORT_MARGIN;
    const fitsBelow  = spaceBelow >= ESTIMATED_MENU_HEIGHT;
    // Se nao cabe embaixo, tenta em cima. Se tambem nao cabe em cima,
    // pega o lado com mais espaco e ajusta com max-height.
    const openAbove = !fitsBelow && spaceAbove > spaceBelow;
    const top = openAbove
      ? Math.max(VIEWPORT_MARGIN, rect.top - 4 - ESTIMATED_MENU_HEIGHT)
      : rect.bottom + 4;
    const left = Math.max(8, rect.right - MENU_WIDTH);
    setMenuPos({ top, left });
  }, [open]);

  // Apos render, se o menu real exceder o viewport, ajusta pra subir
  useEffect(() => {
    if (!open || !menuRef.current || !triggerRef.current) return;
    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const overflowBottom = rect.bottom - (window.innerHeight - VIEWPORT_MARGIN);
    if (overflowBottom > 0) {
      // Tenta empurrar pra cima
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const newTop = Math.max(
        VIEWPORT_MARGIN,
        triggerRect.top - 4 - rect.height
      );
      setMenuPos(p => ({ ...p, top: newTop }));
    }
  }, [open]);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Fecha ao rolar / redimensionar (pra nao desencaixar do trigger)
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <div ref={triggerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="btn btn-ghost btn-sm btn-icon"
      ><ChevronDown size={14} /></button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            width: MENU_WIDTH,
            zIndex: 999,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
            padding: 6,
          }}
        >
          {[
            { icon: Eye,        label: 'Ver detalhes',     fn: onView },
            { icon: Pencil,     label: 'Editar',           fn: onEdit },
            { icon: FileText,   label: 'Visualizar PDF',   fn: onPdf },
            { icon: Paperclip,  label: 'Anexar nota emitida', fn: onAttach },
            ...(hasAnexoNota ? [
              { icon: Mail,     label: 'Enviar NF por e-mail', fn: onSendNF },
            ] : []),
          ].map((a, i) => (
            <button key={i} onClick={() => { a.fn(); setOpen(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 10px', borderRadius: 6, border: 'none', background: 'transparent',
                color: 'var(--ink-2)', fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                cursor: 'pointer', textAlign: 'left',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
            ><a.icon size={14} /> {a.label}</button>
          ))}
          <div className="divider" style={{ margin: '4px 0' }} />
          <div style={{ padding: '6px 10px', fontSize: 10.5, color: 'var(--muted)',
            textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>
            Alterar status
          </div>
          {STATUS_LIST.map(st => (
            <button key={st} onClick={() => { onStatusChange(st); setOpen(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '6px 10px', borderRadius: 6, border: 'none', background: 'transparent',
                fontFamily: 'inherit', fontSize: 12.5, cursor: 'pointer', textAlign: 'left',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
            ><StatusPill status={st} /></button>
          ))}
          <div className="divider" style={{ margin: '4px 0' }} />
          <button onClick={() => { onDelete(); setOpen(false); }}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', borderRadius: 6, border: 'none', background: 'transparent',
              color: 'var(--danger)', fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
              cursor: 'pointer', textAlign: 'left',
            }}
            onMouseOver={e => e.currentTarget.style.background = 'var(--danger-soft)'}
            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
          ><Trash2 size={14} /> Excluir</button>
        </div>,
        document.body
      )}
    </div>
  );
}
